/**
 * A change order the customer reads and signs.
 *
 * Until 2026-09-19 a change order was an internal row and nothing else. It
 * moved the grand total, it printed on the invoice, and the contract warned
 * that one "will be required" — but nothing was ever sent, read or signed, so
 * the customer met it as a bigger number on a bill. This module is the half of
 * putting that right which is a decision rather than a layout: what goes on the
 * page, what the page adds up to, and what the customer still has to do.
 *
 * Three rules from Jonathon, 2026-09-19, and each of them is load-bearing here:
 *
 *  1. **The total moves when the line is typed, not when it is signed.** He was
 *     offered the alternative and said no — the work is being done, so the
 *     money is real. Nothing in this file is an input to `computeLadder`, and
 *     `unsignedChangeOrders` exists to say the uncomfortable half out loud
 *     instead: how much of a total nobody has agreed to.
 *  2. **The rep chooses how it goes out** — email, a copied link in a text, or
 *     the phone handed over — exactly as a presented estimate does. All three
 *     end at one document with one token, so nothing below knows which was
 *     used.
 *  3. **One document, every unsigned line.** Two things found on the same visit
 *     are one page and one signature.
 *
 * The document is frozen at send (ADR 0032): `buildChangeOrderDocument` is
 * given everything and reads nothing, so the page a customer opens tomorrow is
 * the page the rep sent today, whatever the estimate says by then.
 */

import { COMPANY_NAME } from "./constants.js";
import { addCents, cents, formatMoney, toCents, type Cents } from "./money.js";
import type { SigningStep } from "./signingSteps.js";

/** A `change_orders` row, as much of it as any of this needs. */
export interface ChangeOrderRow {
  id: string;
  description?: string | null;
  qty?: unknown;
  unit?: string | null;
  unit_price?: unknown;
  amount?: unknown;
  sort_order?: number | null;
  document_id?: string | null;
  signed_at?: string | null;
}

/** What a line with no description of its own is called. Matches the bill. */
const UNNAMED_LINE = "Roofing work";

/** Sorted the way the estimate lists them, so both sides read alike. */
function inOrder<T extends { sort_order?: number | null }>(
  rows: readonly T[],
): T[] {
  return [...rows].sort(
    (a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100),
  );
}

/**
 * The lines nobody has signed for.
 *
 * Signed is signed whatever became of the document — a line stamped `signed_at`
 * never comes back here, and the receipt that stamped it cannot be edited.
 */
export function unsignedChangeOrders<T extends ChangeOrderRow>(
  rows: readonly T[],
): T[] {
  return inOrder(rows.filter((row) => !row.signed_at));
}

/** What those lines come to. Part of the grand total already — see above. */
export function unsignedChangeOrderTotal(
  rows: readonly ChangeOrderRow[],
): Cents {
  return addCents(
    ...unsignedChangeOrders(rows).map((row) => toCents(row.amount)),
  );
}

/**
 * What the customer HAS agreed to, on top of the contract they signed.
 *
 * The other half of the pair, and the one the money screens need. A signed
 * proposal freezes its total (ADR 0032), so `proposal_acceptances.total` is
 * the contract for ever — and a change order signed a month later is real
 * money the customer owes that no frozen figure can know about. Passed to
 * `invoiceAmount`, which is what every balance on every screen starts from,
 * so that the board, the balance card, the letters and the change order's own
 * page all owe the same number (Jonathon, 2026-09-08: "that remaining balance
 * needs to correspond throughout roofboard").
 *
 * Unsigned lines are deliberately not here. They are inside the estimate's
 * grand total from the moment they are typed (decision 1 above) and the
 * warning says so; they are not something to bill anybody for.
 */
export function signedChangeOrderTotal(
  /*
   * Deliberately looser than `ChangeOrderRow`: the ladder's own input carries
   * the amount and the signature and nothing else (`LadderInput` in
   * ladder.ts), and every screen that needs this figure already has that in
   * its hand. Asking for a whole row would mean a second fetch of rows the
   * caller is holding.
   */
  rows: readonly (Pick<ChangeOrderRow, "amount"> &
    Partial<Pick<ChangeOrderRow, "signed_at">>)[],
): Cents {
  return addCents(
    ...rows
      .filter((row) => Boolean(row.signed_at))
      .map((row) => toCents(row.amount)),
  );
}

/**
 * The line every screen showing the total also shows, or null when there is
 * nothing to warn about.
 *
 * Deliberately a sentence rather than a badge: the number is already in the
 * grand total above it, and "2 unsigned" beside a figure does not tell anybody
 * that part of the figure is not agreed. Zero-amount lines are counted — a
 * change order written at no charge is still something the customer should have
 * said yes to — but a set of them adding to nothing has no sentence, because
 * "$0.00 of this total is not signed" is noise.
 */
export function unsignedChangeOrderWarning(
  rows: readonly ChangeOrderRow[],
): string | null {
  const unsigned = unsignedChangeOrders(rows);
  if (unsigned.length === 0) return null;
  const total = unsignedChangeOrderTotal(rows);
  if (total === 0) return null;
  const many =
    unsigned.length === 1
      ? "1 change order"
      : `${unsigned.length} change orders`;
  return `${formatMoney(total)} of this total is not signed yet — ${many}.`;
}

/** Where a single line has got to, for the column beside it. */
export type ChangeOrderState = "unsent" | "sent" | "viewed" | "signed";

/** The document a line was last sent on, as much as the status needs. */
export interface ChangeOrderDocumentRow {
  id: string;
  status?: string | null;
  viewed_at?: string | null;
}

/**
 * What to show against a line.
 *
 * A voided document counts as never sent, because that is what voiding means:
 * the link is dead and the line is free to go out again on the next one.
 */
export function changeOrderState(
  row: ChangeOrderRow,
  document?: ChangeOrderDocumentRow | null,
): ChangeOrderState {
  if (row.signed_at) return "signed";
  if (!document || !row.document_id) return "unsent";
  if (document.status === "void") return "unsent";
  if (document.status === "viewed" || document.viewed_at) return "viewed";
  if (document.status === "sent") return "sent";
  return "unsent";
}

// ------------------------------------------------------------- withdrawing ---

/**
 * What the office is asked before a change order line is deleted.
 *
 * Jonathon, 2026-09-21 (ADR 0181): deleting a line the customer has signed
 * stays possible, but not silently. The question says the customer signed it,
 * what comes off the total, and that their signed copy will show it withdrawn,
 * so nobody presses OK thinking it is an ordinary row. A line nobody signed
 * gets the ordinary question every other row gets.
 */
export function changeOrderDeleteConfirmation(row: ChangeOrderRow): string {
  if (!row.signed_at) return "Delete this row?";
  return (
    "The customer signed this change order.\n\n" +
    `Deleting it takes ${formatMoney(toCents(row.amount))} off the total, ` +
    "and the customer's signed copy will show it as withdrawn. " +
    "What they signed is kept.\n\n" +
    "Delete it anyway?"
  );
}

/**
 * A signed line deleted afterwards, as the customer's page is told about it.
 * Written by the database (`change_order_withdrawals`), never by the page.
 */
export interface ChangeOrderWithdrawal {
  /** The `change_orders.id` that went — the same id as `lines[].id`. */
  lineId: string;
  withdrawnAt: string;
}

/** What a signed change order comes to once its withdrawn lines are off. */
export interface ChangeOrderAfterWithdrawals {
  withdrawnLineIds: ReadonlySet<string>;
  /** The withdrawn lines' amounts, as the customer signed them. */
  withdrawnTotal: Cents;
  /** `changeTotal` less what was withdrawn. */
  changeTotalNow: Cents;
  /** `contractAfter` less what was withdrawn. */
  contractAfterNow: Cents;
}

/**
 * The signed page's figures with the withdrawn lines taken off, or null when
 * nothing on this document has been withdrawn.
 *
 * The frozen document is never changed (ADR 0032): this works from its own
 * lines and totals, so what the customer signed still reads as signed and the
 * withdrawal is shown beside it. Only withdrawals naming a line on this
 * document count — a stray id takes nothing off.
 */
export function changeOrderAfterWithdrawals(
  document: Pick<
    ChangeOrderDocument,
    "lines" | "changeTotal" | "contractAfter"
  >,
  withdrawals: readonly ChangeOrderWithdrawal[] | null | undefined,
): ChangeOrderAfterWithdrawals | null {
  const named = new Set((withdrawals ?? []).map((w) => w.lineId));
  const withdrawn = document.lines.filter((line) => named.has(line.id));
  if (withdrawn.length === 0) return null;
  const withdrawnTotal = addCents(...withdrawn.map((line) => line.amount));
  return {
    withdrawnLineIds: new Set(withdrawn.map((line) => line.id)),
    withdrawnTotal,
    changeTotalNow: cents(document.changeTotal - withdrawnTotal),
    contractAfterNow: cents(document.contractAfter - withdrawnTotal),
  };
}

// ------------------------------------------------------------ the document ---

/** One line as the customer reads it. Money in cents, like everything here. */
export interface ChangeOrderDocumentLine {
  /** The `change_orders.id` it was built from. Nothing is looked up by it. */
  id: string;
  description: string;
  qty: number;
  unit: string | null;
  unitPrice: Cents;
  amount: Cents;
  /** The product's customer wording, when the line carries one. */
  scope: string | null;
}

/** A paragraph of `change_order_terms`, frozen as it read at send. */
export interface ChangeOrderTerm {
  id: string;
  name: string;
  body: string;
}

/** The frozen page. Stored whole in `change_order_documents.snapshot`. */
export interface ChangeOrderDocument {
  /** "Change order 2" — per job, assigned by the database. */
  number: number;
  /**
   * The day it was sent, as yyyy-mm-dd. A date, never a timestamp: a date
   * column read back through `new Date` is the day before.
   */
  presentedAt: string;
  company: {
    name: string;
    phone: string | null;
    email: string | null;
    address: string | null;
  };
  customer: { name: string; address: string | null };
  job: { number: string | null; address: string | null };
  lines: ChangeOrderDocumentLine[];
  /** What these lines add up to. */
  changeTotal: Cents;
  /** The contract without them. */
  contractBefore: Cents;
  /** The contract with them — what they are agreeing to owe in total. */
  contractAfter: Cents;
  /**
   * What this customer has already paid on the job, as it stood when the rep
   * pressed Present.
   *
   * Optional because it is: every document frozen before 2026-09-22 was built
   * without it, and a page that printed "You have paid $0.00" on one of those
   * would be telling a customer who has paid half their roof that they have
   * paid nothing. Undefined means not known, and nothing is said.
   */
  paidToDate?: Cents;
  /**
   * `contractAfter` less `paidToDate` — what is left on the job once this
   * change is agreed, and the figure the payment screen asks for when they
   * sign.
   *
   * Worked out from this page's own two numbers rather than from the balance
   * card's, so the three lines a customer reads add up in front of them.
   */
  balanceAfter?: Cents;
  terms: ChangeOrderTerm[];
  /** Who they have been dealing with, so the page is signed by a person. */
  rep: { name: string; phone: string | null } | null;
}

export interface BuildChangeOrderDocumentInput {
  number: number;
  presentedAt: string;
  company: ChangeOrderDocument["company"];
  customer: ChangeOrderDocument["customer"];
  job: ChangeOrderDocument["job"];
  /** The unsigned lines. Filtering is the caller's — see `changeOrdersToSend`. */
  lines: readonly ChangeOrderRow[];
  /** What each line's product tells the customer, by change order id. */
  scopes?: Readonly<Record<string, string | null | undefined>>;
  /**
   * The job's grand total as it stands, change orders and all.
   *
   * These lines are already inside it (decision 1 above), so the page works
   * backwards: what it was without them, and what it is with them. Passing a
   * total that excluded them would print a contract figure the customer could
   * not reconcile with the invoice they get later.
   */
  grandTotal: Cents;
  /**
   * What the customer has paid on this job so far — `jobBalance().collected`
   * and nothing else, so the page cannot disagree with the balance card, the
   * board or the letter about how much money has arrived (ADR 0116).
   *
   * Jonathon, 2026-09-22: a change order has to reflect the balance they have
   * already paid. Left out on a job where nobody has paid anything yet, which
   * is most of them, and then the page says nothing about payments at all.
   */
  paidToDate?: Cents | null;
  terms?: readonly ChangeOrderTerm[];
  rep?: ChangeOrderDocument["rep"];
}

/** The lines a document goes out with: everything unsigned, in estimate order. */
export function changeOrdersToSend<T extends ChangeOrderRow>(
  rows: readonly T[],
): T[] {
  return unsignedChangeOrders(rows);
}

/**
 * The whole page, ready to freeze.
 *
 * Everything it needs is passed in — no clock, no fetch — so the same inputs
 * always produce the same document and a test can hold it to the cent.
 */
export function buildChangeOrderDocument(
  input: BuildChangeOrderDocumentInput,
): ChangeOrderDocument {
  const lines: ChangeOrderDocumentLine[] = inOrder(input.lines).map((row) => {
    const amount = toCents(row.amount);
    const qty = Number(row.qty ?? 1);
    const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
    const unitPrice = toCents(row.unit_price);
    return {
      id: row.id,
      description: (row.description ?? "").trim() || UNNAMED_LINE,
      qty: safeQty,
      unit: (row.unit ?? "")?.trim() || null,
      // The unit price is stored, unlike an estimate line's, so it is printed
      // as stored and only worked back when a row predates it.
      unitPrice: unitPrice || cents(amount / safeQty),
      amount,
      scope: (input.scopes?.[row.id] ?? "")?.trim() || null,
    };
  });

  const changeTotal = addCents(...lines.map((line) => line.amount));
  /*
   * Money already in, and what that leaves.
   *
   * Never negative and never more than the job: a customer who has overpaid
   * owes nothing, and "-$400" on a page about money they owe is not a sentence
   * anybody should read. `null` and `undefined` are both "not known" and both
   * leave the two fields off the document entirely — see the interface.
   */
  const paid =
    input.paidToDate == null
      ? null
      : cents(Math.min(Math.max(0, input.paidToDate), input.grandTotal));
  return {
    number: input.number,
    presentedAt: input.presentedAt,
    // The name is frozen onto a page the customer signs, so a blank one (a
    // Present pressed before company settings arrived, or a row nobody filled
    // in) would stay blank for good. Fall back to the constant, as the payment
    // page does: CLAUDE.md rule 11.
    company: {
      ...input.company,
      name: (input.company.name ?? "").trim() || COMPANY_NAME,
    },
    customer: input.customer,
    job: input.job,
    lines,
    changeTotal,
    contractBefore: cents(input.grandTotal - changeTotal),
    contractAfter: input.grandTotal,
    ...(paid == null
      ? {}
      : {
          paidToDate: paid,
          balanceAfter: cents(Math.max(0, input.grandTotal - paid)),
        }),
    // A paragraph nobody has written yet is not printed: an empty body under a
    // heading on something a customer signs reads as a mistake, because it is.
    terms: (input.terms ?? []).filter((term) => term.body.trim() !== ""),
    rep: input.rep ?? null,
  };
}

// ------------------------------------------------------------- the signing ---

export interface ChangeOrderSigningInput {
  /** Whether the terms have been opened on screen. */
  termsOpen: boolean;
  /** The electronic-signature box. */
  consented: boolean;
  /** What they have typed into the name box. */
  signerName: string;
}

/**
 * What the customer still has to do, in page order.
 *
 * The same shape and the same habit as the agreement's list: the
 * page draws it, ticks what is done and points at the one thing to do now,
 * because a greyed-out button and a line of small print is not enough for
 * somebody on a phone at their kitchen table. Four steps rather than the
 * agreement's six — there is no package to choose and no initial box to fill —
 * and the last is never done, because it is done by leaving the page signed.
 */
export function changeOrderSigningSteps(
  input: ChangeOrderSigningInput,
): SigningStep[] {
  return [
    { id: "read", label: "Read the terms", done: input.termsOpen },
    {
      id: "consent",
      label: "Check the box to sign electronically",
      done: input.consented,
    },
    {
      id: "name",
      label: "Type your full name",
      done: input.signerName.trim().length >= 2,
    },
    { id: "sign", label: "Approve this change order", done: false },
  ];
}

/** The one to point at — the first thing not yet done. Never undefined. */
export function nextChangeOrderStep(
  input: ChangeOrderSigningInput,
): SigningStep {
  const steps = changeOrderSigningSteps(input);
  return steps.find((step) => !step.done) ?? steps[steps.length - 1]!;
}

/**
 * May this be signed?
 *
 * The gate for the button, and checked again in the Edge Function before an
 * acceptance is written, and a third time by the database — a customer's page
 * is JavaScript on somebody else's phone. Reading the terms is a step but not a
 * condition, for the same reason the agreement's is not: the button sits below
 * them, so scrolling past is the reading.
 */
export function changeOrderSignatureIsComplete(input: {
  consented: boolean;
  signerName: string;
}): boolean {
  return input.consented && input.signerName.trim().length >= 2;
}
